/**
 * The one place squad COVERAGE becomes words.
 *
 * Coverage and completeness are different measurements that had been rendering
 * as the same shape. The capture header showed `2/70 noted` beside
 * `Partial · 1 of 2`: two "x of y" pills, neither saying what it counted, so a
 * reader had no way to tell which one was the verdict. Both numbers were
 * individually correct — 2 of the 70 squad players are noted, and 1 of the 2
 * noted players is scored — which is why this survived review. The defect was
 * never arithmetic.
 *
 * **Coverage is `noted` of the SQUAD. Completeness is `scored` of the scout's
 * own selection**, and that split is the SE-7286 ruling (Raniel, 2026-08-22):
 * *"the denominator is the squad the scout chose. COMPLETE means 'I scored
 * everyone I picked.'"* Coverage exists to give the reader the squad context
 * that the verdict deliberately excludes — a scout who picked 2 of 70 and
 * scored both has finished their job, and the reader still deserves to know
 * the match had 70 players in it.
 *
 * SE-7550 re-opened this and D3 proposed making the squad the completeness
 * denominator. That was declined: it makes COMPLETE unreachable in practice,
 * which SE-7286 had already considered and rejected in writing. The two
 * numbers stay; each now names what it counts.
 *
 * **Why this is a module and not two template literals.** It already WAS two
 * template literals, and they had drifted: the capture header wrote
 * `${notedCount}/${squadSize} noted` while the submitted view wrote
 * `${notedCount} of ${squadSize} noted` — slash versus "of", the same sentence
 * disagreeing with itself across two screens. The submitted view also guarded
 * a null squad size and the capture header did not, so an unknown squad
 * rendered `2/null noted` on one surface and `2 noted` on the other. Nothing
 * type-checks prose, so the only thing that keeps duplicated copy in step is
 * not duplicating it.
 * @module utils/toSquadCoverageLabel
 */

/**
 * Builds the squad-coverage label.
 *
 * Says "noted", and means it. It read "in squad" until SE-7550's follow-up
 * because the NUMBER it was handed was `tickedCount` — squad membership since
 * SE-7286, where the schema is explicit that "a seeded, untouched player
 * counts". The wording had been made to match the wrong quantity instead of
 * the quantity being fixed, so the pill counted rows on the report while the
 * ratified requirement (D3, Pietra 2026-08-26) and Figma frame 12 both asked
 * for players NOTED — `3/50 noted`.
 *
 * The caller now passes a real noted count, so the pair on a filed report
 * still reads as two different facts — `3 of 50 noted` and `Partial · 1 of 3
 * scored` — and still ends in two different words, which is the property
 * SE-7550 exists to protect.
 * @param notedCount - Players the scout has actually noted, counted off the
 * server's per-evaluation `isNoted`. The NUMERATOR.
 * @param squadSize - Players across both squads, or null when the surface does
 * not know it.
 * @returns The coverage label.
 */
export const toSquadCoverageLabel = (
  notedCount: number,
  squadSize: number | null
): string =>
  // A null squad size is genuinely unknown, not zero. Rendering the numerator
  // alone states only what we measured; `2 of null in squad` and a silent `2 of
  // 0` both state something we did not.
  squadSize === null
    ? `${notedCount} noted`
    : `${notedCount} of ${squadSize} noted`;
